import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/hooks';
import { createClerkSupabaseClient } from '@/lib/supabase';
import { useGetToken } from '@/lib/hooks/use-auth';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { cn } from '@/lib/utils';

// Import step components
import { WelcomeStep } from './steps/welcome-step';
import { CreateOrganizationStep } from './steps/create-organization-step';
import { InviteTeamStep } from './steps/invite-team-step';
import { CreateProjectStep } from './steps/create-project-step';
import { ImportCapTableStep } from './steps/import-cap-table-step';
import { ReviewDashboardStep } from './steps/review-dashboard-step';

interface OnboardingWizardProps {
  onComplete?: () => void;
  onSkip?: () => void;
}

const TOTAL_STEPS = 6;

export function OnboardingWizard({ onComplete, onSkip }: OnboardingWizardProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const getToken = useGetToken();
  const [currentStep, setCurrentStep] = useState(1);
  const [stepData, setStepData] = useState<Record<string, any>>({});
  const [isLoading, setIsLoading] = useState(false);

  // Create authenticated Supabase client using Supabase Auth token
  const supabase = createClerkSupabaseClient(getToken);

  // Load saved onboarding state
  useEffect(() => {
    if (user?.id) {
      loadOnboardingState();
    }
  }, [user?.id]);

  const loadOnboardingState = async () => {
    if (!user?.id) return;
    
    try {
      const { data, error } = await supabase
        .from('user_settings')
        .select('onboarding_step, onboarding_completed')
        .eq('user_id', user.id)
        .maybeSingle(); // Use maybeSingle to handle cases where no record exists

      if (error) {
        console.error('Error loading onboarding state:', error);
        return;
      }

      if (data && !data.onboarding_completed) {
        setCurrentStep(data.onboarding_step || 1);
      }
    } catch (error) {
      console.error('Error loading onboarding state:', error);
    }
  };

  const saveOnboardingState = async (step: number) => {
    if (!user?.id) return;
    
    try {
      const { error } = await supabase
        .from('user_settings')
        .upsert({
          user_id: user.id,
          onboarding_step: step,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'user_id'
        });

      if (error) {
        console.error('Error saving onboarding state:', error);
      }
    } catch (error) {
      console.error('Error saving onboarding state:', error);
    }
  };

  const trackAnalytics = async (eventName: string, eventData?: any) => {
    if (!user?.id) return;
    
    try {
      const { error } = await supabase
        .from('onboarding_analytics')
        .insert({
          user_id: user.id,
          event_name: eventName,
          event_data: eventData || {}
        });

      if (error) {
        console.error('Error tracking analytics:', error);
      }
    } catch (error) {
      console.error('Error tracking analytics:', error);
    }
  };

  const handleNext = async () => {
    if (currentStep < TOTAL_STEPS) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      await saveOnboardingState(nextStep);
      await trackAnalytics(`onboarding_step_${currentStep}_completed`);
    } else {
      await handleComplete();
    }
  };

  const handleBack = async () => {
    if (currentStep > 1) {
      const prevStep = currentStep - 1;
      setCurrentStep(prevStep);
      await saveOnboardingState(prevStep);
    }
  };

  const handleComplete = async () => {
    if (!user?.id) return;
    
    setIsLoading(true);
    try {
      // Mark onboarding as completed
      const { error } = await supabase
        .from('user_settings')
        .update({
          onboarding_completed: true,
          onboarding_step: TOTAL_STEPS,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', user.id);

      if (error) {
        console.error('Error completing onboarding:', error);
        return;
      }

      await trackAnalytics('onboarding_completed', { total_steps: TOTAL_STEPS });

      if (onComplete) {
        onComplete();
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Error completing onboarding:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSkip = async () => {
    if (!user?.id) return;
    
    try {
      const { error } = await supabase
        .from('user_settings')
        .update({
          onboarding_skipped: true,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', user.id);

      if (error) {
        console.error('Error skipping onboarding:', error);
        return;
      }

      await trackAnalytics('onboarding_skipped', { skipped_at_step: currentStep });

      if (onSkip) {
        onSkip();
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Error skipping onboarding:', error);
    }
  };

  const updateStepData = (data: any) => {
    setStepData(prev => ({
      ...prev,
      [`step_${currentStep}`]: data
    }));
  };

  const renderStep = () => {
    const commonProps = {
      data: stepData[`step_${currentStep}`] || {},
      onUpdate: updateStepData,
      onNext: handleNext
    };

    switch (currentStep) {
      case 1:
        return <WelcomeStep {...commonProps} />;
      case 2:
        return <CreateOrganizationStep {...commonProps} />;
      case 3:
        return <InviteTeamStep {...commonProps} organizationId={stepData.step_2?.organizationId} />;
      case 4:
        return <CreateProjectStep {...commonProps} organizationId={stepData.step_2?.organizationId} />;
      case 5:
        return <ImportCapTableStep {...commonProps} projectId={stepData.step_4?.projectId} />;
      case 6:
        return <ReviewDashboardStep {...commonProps} organizationId={stepData.step_2?.organizationId} />;
      default:
        return null;
    }
  };

  const getStepTitle = () => {
    switch (currentStep) {
      case 1: return 'Welcome to Share Lattice';
      case 2: return 'Create Your Organization';
      case 3: return 'Invite Your Team';
      case 4: return 'Create Your First Project';
      case 5: return 'Import or Seed Cap Table';
      case 6: return 'Review Your Dashboard';
      default: return '';
    }
  };

  const progress = (currentStep / TOTAL_STEPS) * 100;

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-2xl">
        <CardHeader>
          <div className="flex items-center justify-between mb-4">
            <CardTitle>{getStepTitle()}</CardTitle>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleSkip}
              className="h-8 w-8"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          <CardDescription>
            Step {currentStep} of {TOTAL_STEPS}
          </CardDescription>
          <Progress value={progress} className="mt-2" />
        </CardHeader>
        
        <CardContent>
          {renderStep()}
        </CardContent>
        
        <CardFooter className="flex justify-between">
          <Button
            variant="outline"
            onClick={handleBack}
            disabled={currentStep === 1}
          >
            <ChevronLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
          
          <div className="flex gap-2">
            <Button
              variant="ghost"
              onClick={handleSkip}
            >
              Skip for now
            </Button>
            <Button
              onClick={handleNext}
              disabled={isLoading}
            >
              {currentStep === TOTAL_STEPS ? 'Complete' : 'Next'}
              {currentStep < TOTAL_STEPS && <ChevronRight className="ml-2 h-4 w-4" />}
            </Button>
          </div>
        </CardFooter>
      </Card>
    </div>
  );
}